import { MemberNameType } from "@msidentity/SISU/model/user";
import { type ICountryInfo } from "@msidentity/SISU/utilities/country-helper";
import type {
  UsernameRecoveryProofState,
  UsernameRecoveryState,
} from "./username-recovery-context";

export enum UsernameRecoveryActionType {
  SetUsernameProof,
  SetAccountsForProofResult,
  SetError,
  SetShortLivedToken,
  SetUsernameDisplay,
  SetHasPrefill,
}

// Set proof string
export type SetUsernameProofAction =
  | {
      type: UsernameRecoveryActionType.SetUsernameProof;
      username: string;
      usernameType: MemberNameType.EASI;
    }
  | {
      type: UsernameRecoveryActionType.SetUsernameProof;
      username: string;
      usernameType: MemberNameType.Phone;
      phoneCountry: ICountryInfo;
    };

type SetAccountsForProofResultAction = {
  type: UsernameRecoveryActionType.SetAccountsForProofResult;
  result: UsernameRecoveryProofState;
};

// Set view error
type SetErrorAction = {
  type: UsernameRecoveryActionType.SetError;
  error: string;
};

type SetShortLivedTokenAction = {
  type: UsernameRecoveryActionType.SetShortLivedToken;
  shortLivedToken: string;
};

type SetUsernameDisplayAction = {
  type: UsernameRecoveryActionType.SetUsernameDisplay;
  usernameDisplay: string;
};

type SetHasPrefillAction = {
  type: UsernameRecoveryActionType.SetHasPrefill;
  hasPrefill: boolean;
};

export type UsernameRecoveryActions =
  | SetUsernameProofAction
  | SetAccountsForProofResultAction
  | SetErrorAction
  | SetShortLivedTokenAction
  | SetUsernameDisplayAction
  | SetHasPrefillAction;

/**
 * UsernameRecovery state reducer.
 * @param state The current UsernameRecovery state
 * @param action The dispatched action that should be processed by this reducer
 * @returns The updated UsernameRecovery state
 */
export default function UsernameRecoveryReducer(
  state: UsernameRecoveryState,
  action: UsernameRecoveryActions,
): UsernameRecoveryState {
  const actionType = action.type;
  switch (actionType) {
    case UsernameRecoveryActionType.SetUsernameProof:
      if (action.usernameType === MemberNameType.EASI) {
        return {
          ...state,
          usernameVal: action.username,
          usernameType: action.usernameType,
        };
      }

      if (action.usernameType === MemberNameType.Phone) {
        return {
          ...state,
          usernameVal: action.username,
          usernameType: action.usernameType,
          phoneCountryState: action.phoneCountry,
        };
      }

      throw new Error(`UsernameRecoveryReducer received unexpected usernametype type`);

    case UsernameRecoveryActionType.SetAccountsForProofResult:
      return {
        ...state,
        proofState: action.result,
      };

    case UsernameRecoveryActionType.SetError:
      return {
        ...state,
        errorState: action.error,
      };

    case UsernameRecoveryActionType.SetShortLivedToken:
      return {
        ...state,
        shortLivedToken: action.shortLivedToken,
      };
    case UsernameRecoveryActionType.SetUsernameDisplay:
      return {
        ...state,
        usernameDisplay: action.usernameDisplay,
      };
    case UsernameRecoveryActionType.SetHasPrefill:
      return {
        ...state,
        hasPrefill: action.hasPrefill,
      };
    default:
      throw new Error(`UsernameRecoveryReducer received unexpected action ${actionType}`);
  }
}
